/**
 * UzaverSmenyFinal — závěrečná obrazovka při ukončení směny.
 *
 * S `inline` se vykreslí **jako pracovní plocha cockpitu**, pod stálým pruhem
 * (stejně jako uzávěrka). Dřív to bylo celostránkové překrytí přes app
 * navigaci: obrazovka se tvářila jako jiná aplikace a zmizelo z ní, kde ve
 * směně člověk je. Bez `inline` zůstává původní fullscreen.
 *
 * Obsah:
 *   1. Hodiny × sazba = záloha (DPP/DPC), pro HPP info že záloha v hotovosti
 *      neexistuje. Přestávka explicitně viditelná.
 *   2. Vydělal jsi celkem + transakce s hlavním flekem (in/out/netto).
 *   3. Detail vyúčtování (rozklikávací) — tržby F/G/B, dýško H/I/C, odvod, manko.
 *   4. Footer s tlačítkem „Odhlásit v HH:MM".
 *
 * Když manažer docházku opravil, je původní hodnota přeškrtnutá přímo u údaje
 * (štítek „upravil manažer", viz `zmenyKorekce`) a tlačítko se mění na
 * „Souhlasím a odhlašuji se v HH:MM". Je to druhá
 * polovina potvrzení, které manažer začal ve své kartě: on rozhodl o číslech,
 * kolega je tímhle stvrdí (`stg_dochazka.souhlas_at`). Notifikace k tomu
 * nechodí — oba u toho stáli.
 *
 * **Docházka se tu nedá měnit.** Čas a sazba určují zálohu mzdy a ta je
 * součástí částky, kterou si manažer s kolegou fyzicky předali při potvrzení
 * vyrovnání. Posunout teď clock_out znamená přepsat peníze, které jsou už
 * venku ze šuplíku. Opravy proto patří o krok dřív — do manažerské karty,
 * kde se o částce rozhoduje. Server to hlídá taky (422).
 *
 * Z téhož důvodu tu není PIN: o penězích bylo rozhodnuto a nic tu na ně
 * nesahá. U hlavní pokladny manažera, kde žádné vyrovnání nepředchází, PIN
 * zůstává — tam se mzda počítá až tady.
 *
 * Po PIN se atomicky:
 *   - zavře fáze ukončení + dokončí checklisty
 *   - aktualizuje docházka (clock_out, hodiny, záloha + případné edity)
 *   - zapíše záloha_vyplacena_at + zaloha_potvrzena_by_user_id
 *   - auto-vrátí vozidlo, nastaví pln_smeny.stav='dokoncena'
 *
 * Props:
 *   smenaId, proposedClockOut (HH:MM z CTA), onCancel (back arrow → cockpit),
 *   onSubmitted(data) (po úspěšném PIN → parent dělá refetch + skryje obrazovku).
 */
import { useEffect, useState, useMemo, useRef } from 'react';
import {
    Clock, Wallet, ChevronLeft, Sparkles, Check, UserCog, Loader2,
} from 'lucide-react';
import ManagerPinDialog from './ManagerPinDialog';
import { SheetButton, SheetAlert } from './SheetUI';
import { chybaZOdpovedi, sekundDoDalsihoPokusu, formatCekani } from './apiChyba';
import { SHEET_TOKENS, getTextColors, getInnerCardSurface } from './sheetStyle';

const fmtKc = (n) => (n == null || isNaN(n)
    ? '—'
    : Math.round(Number(n)).toLocaleString('cs-CZ') + ' Kč');

const fmtHodMin = (hodinyDecimal) => {
    if (hodinyDecimal == null || isNaN(hodinyDecimal)) return '—';
    const total = Math.round(hodinyDecimal * 60);
    const h = Math.floor(total / 60);
    const m = total - h * 60;
    return `${h} h ${String(m).padStart(2, '0')} min`;
};

const isoToHHMM = (iso) => {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    } catch { return ''; }
};

export default function UzaverSmenyFinal({
    smenaId, proposedClockOut,         // HH:MM string — co je na CTA tlačítku
    pokladnaSmena = null,              // kvůli čekání na manažera (viz níž)
    cekaNaVyrovnani = false,           // bez pokladny: čeká na předání zálohy
    pozadatOVyrovnani = false,         // …a rovnou si o něj říct (klik byl v kartě)
    onCancel, onSubmitted,             // zpět → cockpit; refetch po úspěšném potvrzení
    onCekaNaManazera,                  // požádáno o vyrovnání → cockpit přepne do čekání
    inline = false,                    // vykreslit jako plochu cockpitu, ne překrytí
    isDark, isMobile,
}) {
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    const [preview, setPreview] = useState(null);
    const [loading, setLoading] = useState(true);
    const [fetchError, setFetchError] = useState(null);
    const [submitError, setSubmitError] = useState(null);

    const [edit, setEdit] = useState({ clock_in: '', clock_out: '', sazba_kc_h: '', prestavka_min: '' });

    const [pinDialogOpen, setPinDialogOpen] = useState(false);
    const [odesilam, setOdesilam] = useState(false);
    // Sekundy do dalšího povoleného pokusu po 429. Dokud běží, je tlačítko
    // zamčené — jinak se na červenou hlášku odpovídá mačkáním, které limit
    // jen dál drží nahoře (a přesně tak vznikla série dvanácti pokusů).
    const [cekaniS, setCekaniS] = useState(0);
    useEffect(() => {
        if (cekaniS <= 0) return undefined;
        const t = setTimeout(() => setCekaniS((s) => s - 1), 1000);
        return () => clearTimeout(t);
    }, [cekaniS]);

    // Čekání na manažera patří **sem**, ne do cockpitu. Tahle obrazovka se
    // otevírá hned po odeslání uzávěrky, tedy dřív, než manažer vyúčtování
    // potvrdí — a tlačítko „Odhlásit" tou dobou vrací ze serveru červenou 422.
    // Rozvozce tak mačkal naslepo dokola. Teď nese jméno překážky a odemkne
    // se samo: parent refetchne na eventu `VyuctovaniSchvaleno` a `stav` fleku
    // se překlopí na `uzavreno`.
    //
    // Hlavní pokladna manažera tuhle bránu nemá — nikdo mu nic nepotvrzuje.
    // Bez pokladny je to stejné čekání, jen se pozná z docházky: kolega si
    // klepnutím na „Odhlásit" řekl o zálohu a manažer ji ještě nepotvrdil.
    const cekaNaManazera = cekaNaVyrovnani || (!!pokladnaSmena
        && pokladnaSmena.typ !== 'bistro_hlavni'
        && pokladnaSmena.stav === 'ceka_na_schvaleni');

    useEffect(() => {
        if (!smenaId) return;
        let cancelled = false;
        setLoading(true);
        setFetchError(null);
        fetch(`/api/me/smena/${smenaId}/uzavrit-preview`)
            .then(async r => {
                const data = await r.json().catch(() => ({}));
                if (!r.ok) throw new Error(data?.error || `HTTP ${r.status}`);
                return data;
            })
            .then(d => {
                if (cancelled) return;
                setPreview(d);
                // Default clock_out = `proposedClockOut` (= co je na tlačítku
                // "Odhlásit v X", buď endTimeOverride nebo plánovaný cas_do).
                // Backend vrací clock_out_navrh = now() jen pro fallback, ale
                // ten chceme ignorovat — drží to logiku "směna se zavírá v čas
                // dle plánu / manuálního zadání", ne v čas kdy uživatel klikl.
                setEdit({
                    clock_in: isoToHHMM(d.clock_in),
                    // Potvrzený čas má přednost před tím z tlačítka: manažer
                    // podle něj spočítal mzdu, když peníze předával.
                    clock_out: isoToHHMM(d.navrzeny_clock_out) || proposedClockOut || isoToHHMM(d.clock_out_navrh),
                    sazba_kc_h: d.sazba_kc_h != null ? String(d.sazba_kc_h) : '',
                    prestavka_min: d.prestavka_min != null ? String(d.prestavka_min) : '',
                });
            })
            .catch(e => { if (!cancelled) setFetchError(e.message || 'Načtení selhalo.'); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [smenaId, pokladnaSmena?.stav, cekaNaVyrovnani]);

    // Kolega bez pokladny, kterému se vyplácí záloha: na tuhle obrazovku se
    // dostal klepnutím na „Odhlásit" (v kartě, nebo přes vlastní čas) — takže
    // si o vyrovnání říká už tím a obrazovka se otevře rovnou s čekáním.
    // Poznává se to z náhledu, ne z cesty, kterou sem přišel: dřív to uměla
    // jen karta a volba vlastního času žádost neposlala.
    //
    // Až s načteným náhledem: teprve z něj je v `edit.clock_out` čas konce,
    // se kterým kolega odchází (bez něj by server dostal „teď") — a částka
    // zálohy, která rozhoduje, jestli je vůbec co vyrovnávat. Posílá se
    // jednou za život obrazovky; server je k opakování odolný taky.
    const zalohaKVyrovnani = !pokladnaSmena
        && ['DPP', 'DPC'].includes(preview?.typ_uvazku)
        && Number(preview?.castka_zaloha_kc ?? 0) > 0;
    const pozadano = useRef(false);
    useEffect(() => {
        if (!preview || cekaNaManazera || pozadano.current) return;
        if (!pozadatOVyrovnani && !zalohaKVyrovnani) return;
        pozadano.current = true;
        // Výsledek se dřív zahazoval. Když tenhle request neprošel (a 429 mu
        // stál v cestě zrovna tak jako čemukoli jinému), obrazovka mlčela a
        // kolega čekal na manažera, kterému se žádný úkol neobjevil.
        odesli().then((r) => {
            if (!r?.ok) setSubmitError(r?.error || 'Žádost o vyrovnání se nepodařilo odeslat.');
        });
    }, [preview, pozadatOVyrovnani, zalohaKVyrovnani, cekaNaManazera]);

    // Klient-side přepočet po editaci (live preview celkové částky bez PIN volání).
    const liveComputed = useMemo(() => {
        if (!preview) return null;
        const sazba = parseFloat(edit.sazba_kc_h);
        const prestavka = parseInt(edit.prestavka_min, 10);
        const datum = preview.datum;
        const clockInDate = parseHHMMonDate(datum, edit.clock_in) || new Date(preview.clock_in);
        const clockOutDate = parseHHMMonDate(datum, edit.clock_out) || new Date(preview.clock_out_navrh);
        const rawMin = Math.max(0, (clockOutDate - clockInDate) / 60000);
        const prestavkaMin = Number.isFinite(prestavka) ? prestavka : preview.prestavka_min;
        const hodiny = Math.max(0, (rawMin - prestavkaMin) / 60);
        const finalSazba = Number.isFinite(sazba) ? sazba : preview.sazba_kc_h;
        const castkaZaloha = preview.typ_uvazku === 'DPP' || preview.typ_uvazku === 'DPC'
            ? Math.round(hodiny * (finalSazba || 0))
            : null;
        // `upraveno` = manažer skutečně změnil hodnotu oproti defaultu. Default
        // pro clock_out je proposedClockOut (tlačítko), ne backend.clock_out_navrh.
        const defaultClockOut = proposedClockOut || isoToHHMM(preview.clock_out_navrh);
        const upraveno = edit.clock_in !== isoToHHMM(preview.clock_in)
                      || edit.clock_out !== defaultClockOut
                      || String(preview.sazba_kc_h ?? '') !== edit.sazba_kc_h
                      || String(preview.prestavka_min ?? '') !== edit.prestavka_min;
        return { hodiny, prestavkaMin, finalSazba, castkaZaloha, upraveno };
    }, [preview, edit, proposedClockOut]);

    // Vyrovnání s hlavní pokladnou — jedno jasné číslo „k odevzdání":
    //   k odevzdání = hotovost nad základ − net dýško/manko − záloha mzdy
    //
    // `net` je znaménkové: kladné = dýško (řidič si nechá, odevzdá míň),
    // záporné = manko (řidič dorovná, odevzdá víc). Model je odolný vůči záměně
    // hotovost↔karta — net se počítá ze součtu F+G a terminálu, ne z rozdělení.
    //
    // Záloha závisí na zadaném čase konce (liveComputed) → číslo se přepočítává.
    // Kladné = řidič odevzdá manažerovi, záporné = dostane od manažera.
    const summary = useMemo(() => {
        if (!preview || !liveComputed) return null;
        const p = preview.pokladna || {};
        const zaloha = liveComputed.castkaZaloha || 0;
        // Hlavní pokladna — manažer neodevzdává, jen dýško z přepočtu.
        if (p.je_hlavni) {
            return { jeHlavni: true, dysko: p.dysko_kc, zaloha };
        }
        const hotovost = p.hotovost_predana_kc || 0;   // E − základ
        const net = p.net_dysko_manko_kc || 0;         // znaménkové dýško/manko
        const kOdevzdani = Math.round((hotovost - net - zaloha) * 100) / 100;
        // Papírové účtenky jdou k manažerovi spolu s penězi.
        return { hotovost, net, zaloha, kOdevzdani, uctenky: p.uctenky_pocet || 0 };
    }, [preview, liveComputed]);
    // Pro PIN dialog — co řidič fyzicky dostane (kladná částka), pokud vůbec.
    const cashToReceive = (summary && !summary.jeHlavni) ? -summary.kOdevzdani : 0;

    const odesli = async (pin = null) => {
        setSubmitError(null);
        // Posílá se jen čas konce — ten na tlačítku, ne `now()` z backendu.
        // Backend bez něj sáhne po navrženém času z uzávěrky, případně po teď.
        // Nic dalšího: docházku tahle obrazovka nemění (viz hlavička souboru).
        // Příznak říká serveru „umím čekat na manažera". Bez něj (stará
        // verze načtená v telefonu) server drží původní postup s PINem, ať
        // se kolega uprostřed směny nedozví, že je odhlášený, když není.
        const body = { vyrovnani_bez_pokladny: true };
        if (edit.clock_out) body.dochazka_edit = { clock_out: edit.clock_out };
        if (pin) body.manager_pin = pin;

        const csrf = document.querySelector('meta[name="csrf-token"]')?.content;
        try {
            const r = await fetch(`/api/me/smena/${smenaId}/uzavrit-smenu`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'X-CSRF-TOKEN': csrf, Accept: 'application/json' },
                body: JSON.stringify(body),
            });
            const data = await r.json().catch(() => ({}));
            if (!r.ok) {
                // 429 nese `Retry-After`; předáme ho výš, ať se tlačítko zamkne
                // přesně na tu dobu a nebliká „zkus to za 45 s" u aktivního CTA.
                if (r.status === 429) setCekaniS(sekundDoDalsihoPokusu(r));
                return { ok: false, status: r.status, error: chybaZOdpovedi(r, data) };
            }
            // 202 = „řekl sis o vyrovnání, teď čekáš". Odhlášení proběhne až
            // druhým klepnutím, po manažerově potvrzení — proto se obrazovka
            // nezavírá a jen se překreslí do čekání (parent refetchne).
            if (data?.ceka_na_manazera) {
                setPinDialogOpen(false);
                onCekaNaManazera?.();
                return { ok: true, ceka: true };
            }
            setPinDialogOpen(false);
            // Rovnou dál. Mezistránka „Směna uzavřena 🎉" tu byla šest sekund
            // a říkala totéž co obrazovka za ní („Dnes máš odpracováno“);
            // oslava, kterou musíš odklepnout, není oslava. Čísla ze `data`
            // si přebírá parent a ukáže je tam.
            onSubmitted?.(data);
            return { ok: true };
        } catch (e) {
            return { ok: false, error: e?.message || 'Síťová chyba' };
        }
    };

    // Inline = plocha modulu pod stálým pruhem: žádný povrch, žádné pozadí
    // (nese ho záře aplikace), žádné odsazení k výřezu — to má pruh nad ní.
    // Fullscreen varianta zůstává pro místa, kde nad sebou pruh nemá.
    return (
        <div style={inline ? {
            flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column',
        } : {
            position: 'fixed', inset: 0, zIndex: 1500,
            background: isDark ? '#0a0a0d' : '#ffffff',
            display: 'flex', flexDirection: 'column',
            paddingTop: 'calc(env(safe-area-inset-top, 0px) * 0.9)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
        }}>
            {/* Po PIN success — celostránkové potvrzení. Po pár sekundách se
                auto-dismiss a cockpit přepne na další naplánovanou směnu. */}
            {<>
            {/* Hlavička jen v překrytí. Na ploše cockpitu ji nese pruh nad
                obsahem — druhý nadpis pod ním by jen ukrajoval z výšky. */}
            {!inline && (
            <div style={{
                flexShrink: 0,
                padding: '14px 16px',
                borderBottom: `1px solid ${colors.divider}`,
                display: 'flex', alignItems: 'center', gap: 10,
            }}>
                <button type="button" onClick={onCancel} style={{
                    width: 36, height: 36, borderRadius: 7,
                    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    background: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                    border: `1px solid ${colors.divider}`,
                    color: colors.textPrimary, cursor: 'pointer', flexShrink: 0,
                }}>
                    <ChevronLeft size={20} />
                </button>
                <span style={{
                    fontSize: 17, fontWeight: 800, color: colors.textPrimary,
                    letterSpacing: -0.2,
                }}>Závěr směny</span>
            </div>
            )}

            {/* Loader jen když ještě není co ukázat. Obrazovka se načítá znovu
                i ve chvíli, kdy manažer potvrdí vyrovnání (kvůli jeho korekci
                docházky) — blbnout tehdy obsah na „Načítám…" by probliklo
                přesně v okamžiku, kdy se člověk dívá, jestli už může jít. */}
            {loading && !preview && (
                <div style={{ flex: 1, padding: 24, textAlign: 'center',
                    color: colors.textMuted, fontSize: 13 }}>
                    Načítám…
                </div>
            )}

            {fetchError && (
                <div style={{ flex: 1, padding: 16 }}>
                    <SheetAlert kind="error" isDark={isDark}>{fetchError}</SheetAlert>
                </div>
            )}

            {!fetchError && preview && liveComputed && (
                <div style={{
                    flex: 1, overflowY: 'auto', minHeight: 0,
                    padding: 16,
                    display: 'flex', flexDirection: 'column', gap: SHEET_TOKENS.spacing.sectionGap,
                    maxWidth: 560, width: '100%', margin: '0 auto',
                }}>
                    {/* Hodiny + sazba + záloha. Co manažer přepsal, je
                        přeškrtnuté přímo u údaje — viz `zmenyKorekce`. */}
                    <HoursCard
                        preview={preview}
                        liveComputed={liveComputed}
                        edit={edit}
                        zmeny={zmenyKorekce(preview)}
                        isDark={isDark}
                    />

                    {/* HLAVNÍ ZÁVĚR — 3 řádky: odevzdat / dostat / netto.
                        Tady leží jádro zprávy pro rozvozce — kolik fyzicky dá
                        manažerovi a co dostane zpět. Po PIN se tato transakce
                        propíše do hlavního fleku. */}
                    <SummaryCard
                        summary={summary}
                        isDark={isDark}
                    />

                    {submitError && (
                        <SheetAlert kind="error" isDark={isDark}>{submitError}</SheetAlert>
                    )}
                </div>
            )}

            {/* Odhlášení. PIN se neptáme dopředu — u kolegy s potvrzeným
                vyrovnáním žádný netřeba, u hlavní pokladny si ho vyžádá server
                (409) a teprve pak se otevře dialog. */}
            {!fetchError && preview && (
                <div style={{
                    flexShrink: 0,
                    padding: '12px 16px 14px',
                    borderTop: `1px solid ${colors.divider}`,
                }}>
                    {/* Dokud manažer nepotvrdí předání peněz, tlačítko tu
                        není — jen čekání. Zamčené tlačítko svádělo mačkat ho
                        dokola; točící se kolečko říká „děje se to, počkej".
                        Po potvrzení se tlačítko objeví samo (event, pojistkou
                        dotaz každé 4 s — viz MojeSmena/index.jsx). */}
                    {cekaNaManazera ? (
                        <div role="status" aria-live="polite" style={{
                            minHeight: 52, borderRadius: SHEET_TOKENS.radius.input,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 10,
                            padding: '8px 14px',
                            background: isDark ? 'rgba(245,158,11,0.10)' : 'rgba(245,158,11,0.08)',
                            border: '1px solid rgba(245,158,11,0.35)',
                        }}>
                            <Loader2 size={18} className="animate-spin" style={{ color: '#F59E0B', flexShrink: 0 }} />
                            <span style={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                                    Čekám na manažera
                                </span>
                                <span style={{ fontSize: 11.5, color: colors.textMuted }}>
                                    Až potvrdí předání peněz, objeví se tu Odhlásit.
                                </span>
                            </span>
                        </div>
                    ) : (
                    <SheetButton
                        isDark={isDark}
                        variant="primary"
                        accent="#10B981"
                        accent2="#1D9E75"
                        icon={<Check size={16} />}
                        disabled={odesilam || cekaniS > 0}
                        onClick={async () => {
                            if (odesilam || cekaniS > 0) return;
                            setOdesilam(true);
                            const r = await odesli();
                            setOdesilam(false);
                            if (r.ok) return;
                            if (r.status === 409 || r.status === 403) { setPinDialogOpen(true); return; }
                            setSubmitError(r.error);
                        }}
                    >
                        {odesilam
                            ? 'Odhlašuji…'
                            : cekaniS > 0
                                ? `Zkus to za ${formatCekani(cekaniS)}`
                                // Po korekci tlačítko nese souhlas, ne jen odchod:
                                // je to druhá polovina potvrzení, které manažer
                                // začal ve své kartě.
                                : preview.upraveno_at
                                    ? `Souhlasím a odhlašuji se v ${edit.clock_out || proposedClockOut || ''}`
                                    : `Odhlásit v ${edit.clock_out || proposedClockOut || ''}`}
                    </SheetButton>
                    )}
                </div>
            )}

            </>}

            {pinDialogOpen && (
                <ManagerPinDialog
                    castka={Math.max(0, Math.round(cashToReceive))}
                    onClose={() => setPinDialogOpen(false)}
                    onSubmit={(pin) => odesli(pin)}
                    isDark={isDark}
                />
            )}
        </div>
    );
}

// ─── HoursCard — odpracovaný čas, prestavka, sazba, záloha ────────────────────

function HoursCard({ preview, liveComputed, edit, zmeny = null, isDark }) {
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);
    // Původní hodnota přeškrtnutá těsně před novou — „20:30 20:00".
    // Čára musí být vidět i na telefonu: tenká šedá přes šedé číslo se na
    // displeji ztrácela. Tlustší a červená, číslo pod ní čitelné.
    const puvodne = (v) => v != null && (
        <span style={{
            textDecorationLine: 'line-through',
            textDecorationColor: isDark ? '#F87171' : '#DC2626',
            textDecorationThickness: 2,
            color: colors.textSecondary, fontWeight: 600, marginRight: 6,
        }}>{v}</span>
    );
    // …a nová hodnota zvýrazněná, ať je jasné, která platí.
    const nove = (zmena, v) => (zmena != null
        ? <strong style={{ color: colors.textPrimary, fontWeight: 700 }}>{v}</strong>
        : v);
    const isHpp = preview.typ_uvazku === 'HPP';
    const ci = edit.clock_in || isoToHHMM(preview.clock_in);
    const co = edit.clock_out || isoToHHMM(preview.clock_out_navrh);

    return (
        <div style={{
            padding: 14, borderRadius: SHEET_TOKENS.radius.card,
            background: inner.background, border: inner.border,
            display: 'flex', flexDirection: 'column', gap: 8,
        }}>
            <div style={{
                display: 'inline-flex', alignItems: 'center', gap: 6,
                fontSize: SHEET_TOKENS.typography.label.size,
                fontWeight: SHEET_TOKENS.typography.label.weight,
                letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                textTransform: SHEET_TOKENS.typography.label.textTransform,
                color: colors.labelMuted,
            }}>
                <Clock size={12} style={{ color: '#3B82F6' }} />
                Odpracovaný čas
                {/* Korekce manažera — jen štítek; co se změnilo, ukazují
                    přeškrtnuté údaje níž. */}
                {zmeny && (
                    <span style={{
                        marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 4,
                        textTransform: 'none', letterSpacing: 0, fontWeight: 600, color: '#D97706',
                    }}>
                        <UserCog size={12} /> upravil manažer
                    </span>
                )}
            </div>

            <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontSize: 22, fontWeight: 800, color: colors.textPrimary,
                    fontVariantNumeric: 'tabular-nums', letterSpacing: -0.4 }}>
                    {fmtHodMin(liveComputed.hodiny)}
                </span>
                <span style={{ fontSize: 12, color: colors.textMuted, fontVariantNumeric: 'tabular-nums' }}>
                    {puvodne(zmeny?.prichod)}{nove(zmeny?.prichod, ci || '—')} → {puvodne(zmeny?.odchod)}{nove(zmeny?.odchod, co || '—')}
                </span>
            </div>

            {/* Přestávka — explicitní řádek aby uživatel viděl proč hodiny míň. */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '6px 8px', borderRadius: 5,
                background: liveComputed.prestavkaMin > 0
                    ? (isDark ? 'rgba(255,138,0,0.10)' : 'rgba(255,138,0,0.06)')
                    : 'transparent',
                fontSize: 11.5, color: colors.textSecondary,
            }}>
                <span>Přestávka neplacená</span>
                <strong style={{
                    color: liveComputed.prestavkaMin > 0 ? '#FF8A00' : colors.textMuted,
                    fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                }}>
                    {puvodne(zmeny?.prestavka)}{liveComputed.prestavkaMin > 0 ? `− ${liveComputed.prestavkaMin} min` : '0 min'}
                </strong>
            </div>

            {!isHpp && liveComputed.finalSazba > 0 && liveComputed.castkaZaloha != null && (
                <div style={{
                    marginTop: 4, padding: '10px 12px', borderRadius: SHEET_TOKENS.radius.input,
                    background: 'rgba(16,185,129,0.10)', border: '1px solid rgba(16,185,129,0.30)',
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}>
                    <span style={{ fontSize: 12.5, color: colors.textSecondary }}>
                        {fmtHodMin(liveComputed.hodiny)} × {puvodne(zmeny?.sazba)}{nove(zmeny?.sazba, liveComputed.finalSazba)} Kč/h
                    </span>
                    <span style={{ fontSize: 18, fontWeight: 800, color: '#10B981', fontVariantNumeric: 'tabular-nums' }}>
                        {fmtKc(liveComputed.castkaZaloha)}
                    </span>
                </div>
            )}
            {isHpp && (
                <div style={{ fontSize: 11.5, color: colors.textMuted, fontStyle: 'italic' }}>
                    HPP — záloha v hotovosti se nevyplácí, mzda jde přes účtárnu.
                </div>
            )}
        </div>
    );
}

// ─── zmenyKorekce — co manažer přepsal ───────────────────────────────────────

/**
 * Manažerova korekce docházky na obrazovce toho, koho se týká — původní
 * hodnoty polí, která se opravdu liší (`null` = beze změny).
 *
 * Měnit se tu nedá nic — o penězích bylo rozhodnuto u pultu a server to hlídá
 * (422). Kolega ale musí **vidět, s čím souhlasí**: bez původní hodnoty by
 * potvrzoval čísla, která nikdy nezadal. Dřív to nesl samostatný oranžový
 * blok nad kartou; teď je původní hodnota přeškrtnutá přímo u údaje, kterého
 * se týká, a nad kartou stojí jen štítek „upravil manažer".
 *
 * @returns {{prichod, odchod, prestavka, sazba}|null} null = nic se nezměnilo
 */
function zmenyKorekce(preview) {
    if (!preview?.upraveno_at) return null;
    const p = preview.puvodni || {};
    const lisi = (pred, po) => (pred != null && pred !== '' && po != null && String(pred) !== String(po) ? pred : null);
    const z = {
        prichod: lisi(isoToHHMM(p.clock_in), isoToHHMM(preview.clock_in)),
        odchod: lisi(isoToHHMM(p.clock_out), isoToHHMM(preview.navrzeny_clock_out)),
        prestavka: lisi(p.prestavka_min, preview.prestavka_min) != null ? `${p.prestavka_min} min` : null,
        sazba: lisi(p.sazba_kc_h, preview.sazba_kc_h),
    };
    // Příznak `upraveno_at` může sedět i na korekci, která se po přepočtu
    // nikde neprojeví — pak není co ukazovat.
    return Object.values(z).some((v) => v != null) ? z : null;
}

// ─── SummaryCard — vyrovnání s hlavní pokladnou ───────────────────────────────
//
// Jádro závěrečné obrazovky — jedno jasné číslo „k odevzdání":
//
//   + Celá hotovost na pokladnu             2 380 Kč
//   − Dýško (hotovost + karta + online)       200 Kč
//   − Záloha mzdy                             450 Kč
//   ─────────────────────────────────────
//   = K odevzdání manažerovi                1 730 Kč   (velký, dle znaménka)
//
// Záloha se počítá živě dle zadaného času konce směny → číslo se přepočítává.

function SummaryCard({ summary, isDark }) {
    const colors = getTextColors(isDark);
    const inner = getInnerCardSurface(isDark);

    // Manažer hlavní pokladny — neodevzdává; ukážeme čisté dýško z přepočtu
    // pokladny. Bez přepočtu (dysko == null) se karta nezobrazí.
    if (summary.jeHlavni) {
        if (summary.dysko == null) return null;
        const d = summary.dysko;
        const hlAccent = d >= 0 ? '#10B981' : '#EF4444';
        return (
            <div style={{
                padding: 16, borderRadius: SHEET_TOKENS.radius.card,
                background: inner.background, border: inner.border,
                display: 'flex', flexDirection: 'column', gap: 10,
            }}>
                <div style={{
                    fontSize: SHEET_TOKENS.typography.label.size,
                    fontWeight: SHEET_TOKENS.typography.label.weight,
                    letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                    textTransform: SHEET_TOKENS.typography.label.textTransform,
                    color: colors.labelMuted,
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                }}>
                    <Wallet size={12} style={{ color: '#FF8A00' }} />
                    Přepočet hlavní pokladny
                </div>
                <div style={{
                    padding: '14px 16px', borderRadius: SHEET_TOKENS.radius.input,
                    background: isDark ? `${hlAccent}14` : `${hlAccent}10`,
                    border: `1px solid ${hlAccent}40`,
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
                }}>
                    <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                        {d >= 0 ? 'Tvoje dýško' : 'Manko'}
                    </span>
                    <span style={{
                        fontSize: 24, fontWeight: 800, color: hlAccent,
                        fontVariantNumeric: 'tabular-nums', letterSpacing: -0.4, lineHeight: 1,
                    }}>
                        {fmtKc(Math.abs(d))}
                    </span>
                </div>
            </div>
        );
    }

    const k = summary.kOdevzdani;
    const odevzdava = k >= 0;                       // kladné = řidič odevzdá
    const accent = odevzdava ? '#FF8A00' : '#10B981';

    return (
        <div style={{
            padding: 16, borderRadius: SHEET_TOKENS.radius.card,
            background: inner.background, border: inner.border,
            display: 'flex', flexDirection: 'column', gap: 10,
        }}>
            <div style={{
                fontSize: SHEET_TOKENS.typography.label.size,
                fontWeight: SHEET_TOKENS.typography.label.weight,
                letterSpacing: SHEET_TOKENS.typography.label.letterSpacing,
                textTransform: SHEET_TOKENS.typography.label.textTransform,
                color: colors.labelMuted,
                display: 'inline-flex', alignItems: 'center', gap: 6,
            }}>
                <Wallet size={12} style={{ color: '#FF8A00' }} />
                Co si předáte
            </div>

            {/* Záporné: v tašce je méně než základ (platil jsi z ní účtenky) —
                manažer ho doplní, proto to jde k „dostaneš". */}
            {summary.hotovost >= 0
                ? <CalcRow label="Hotovost, kterou máš u sebe" sign="+" value={summary.hotovost} colors={colors} />
                : <CalcRow label="Doplnění základu (platil jsi z tašky)" sign="−" value={-summary.hotovost} colors={colors} />}
            {summary.net >= 0
                ? <CalcRow label="Tvoje dýško" sign="−" value={summary.net} colors={colors} />
                : <CalcRow label="Chybějící hotovost" sign="+" value={-summary.net} colors={colors} />}
            <CalcRow label="Mzda za dnešek" sign="−" value={summary.zaloha} colors={colors} />

            <div style={{ borderTop: `1px dashed ${colors.divider}`, margin: '2px 0' }} />

            {/* Výsledek — velké číslo k odevzdání / k obdržení */}
            <div style={{
                padding: '14px 16px', borderRadius: SHEET_TOKENS.radius.input,
                background: isDark ? `${accent}14` : `${accent}10`,
                border: `1px solid ${accent}40`,
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8,
            }}>
                <span style={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                    {odevzdava ? 'Dáš manažerovi' : 'Dostaneš od manažera'}
                </span>
                <span style={{
                    fontSize: 24, fontWeight: 800, color: accent,
                    fontVariantNumeric: 'tabular-nums', letterSpacing: -0.4, lineHeight: 1,
                }}>
                    {fmtKc(Math.abs(k))}
                </span>
            </div>

            {summary.uctenky > 0 && (
                <div style={{ fontSize: 12.5, fontWeight: 600, color: colors.textSecondary, padding: '0 4px' }}>
                    {summary.uctenky === 1
                        ? '+ přidej účtenku'
                        : `+ přidej ${summary.uctenky} ${summary.uctenky < 5 ? 'účtenky' : 'účtenek'}`}
                </div>
            )}

            {summary.net < 0 && (
                <div style={{
                    fontSize: 11.5, color: colors.textMuted, fontStyle: 'italic',
                    lineHeight: 1.4, padding: '0 4px',
                }}>
                    Chybí <strong>{fmtKc(-summary.net)}</strong> — vyřešíš to
                    s manažerem, až si budete peníze předávat.
                </div>
            )}
        </div>
    );
}

function CalcRow({ label, sign, value, colors, danger = false }) {
    return (
        <div style={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', gap: 8 }}>
            <span style={{ fontSize: 12.5, color: colors.textSecondary, lineHeight: 1.3 }}>
                {label}
            </span>
            <span style={{
                fontSize: 14, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
                color: danger ? colors.danger : colors.textPrimary, whiteSpace: 'nowrap',
            }}>
                {sign}&nbsp;{fmtKc(value)}
            </span>
        </div>
    );
}

// ─── Drobné helpers ───────────────────────────────────────────────────────────

function parseHHMMonDate(datum, hhmm) {
    if (!datum || !hhmm || !/^\d{2}:\d{2}$/.test(hhmm)) return null;
    const [y, m, d] = String(datum).slice(0, 10).split('-').map(Number);
    const [hh, mm] = hhmm.split(':').map(Number);
    return new Date(y, m - 1, d, hh, mm, 0);
}
